// ==================== GURUKUL — PROGRAMS & COURSES (admin / OPD) ====================
// The learning catalog: Programs offer Learning Paths; a path holds courses by month; a certificate is a
// set of courses (from any paths). Nothing is deleted — switch things off instead. LMS wave, Phase A.
import { showToast, showConfirm, showPrompt } from '../components/toasts.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || 'Request failed'); e.errors = data.errors; throw e; }
  return data;
}
const post = body => api('/api/gurukul/catalog', { method: 'POST', body: JSON.stringify(body) });
const failed = err => showToast(err.message + (err.errors?.length ? ' — ' + err.errors.slice(0, 5).join('; ') + (err.errors.length > 5 ? ' …' : '') : ''), 'error');

let data = { programs: [], paths: [], certificates: [] };
let tab = 'programs';

const tabStyle = on => `background:none;border:none;padding:8px 16px;cursor:pointer;${on ? 'font-weight:bold;border-bottom:3px solid var(--primary);color:var(--text);' : 'color:var(--text-secondary);'}`;
const activeBadge = a => a ? '<span class="badge badge-success">on</span>' : '<span class="badge badge-danger">off</span>';

export function getGurukulCatalogPage() {
  return `
    <h1 class="page-title">Programs &amp; courses</h1>
    <p class="page-intro">A <strong>Program</strong> offers <strong>Learning Paths</strong>; each path holds its courses month by month.
      A <strong>Certificate</strong> is a set of courses, which may come from different paths. Nothing here is ever deleted — switch it off instead.</p>
    <div class="tabs-container" style="margin-bottom:16px;display:flex;gap:6px;flex-wrap:wrap;border-bottom:1px solid var(--border);padding-bottom:8px;">
      <button type="button" class="tab-btn" data-tab="programs">Programs</button>
      <button type="button" class="tab-btn" data-tab="paths">Learning Paths</button>
      <button type="button" class="tab-btn" data-tab="certificates">Certificates</button>
    </div>
    <div id="gkCatBody"><p class="empty-state">Loading…</p></div>
    <div id="gkCatModal" class="modal"><div class="modal-content" style="max-width:860px;">
      <button type="button" class="close-modal" id="gkCatClose">&times;</button>
      <h2 id="gkCatModalTitle"></h2><div id="gkCatModalBody"></div>
    </div></div>`;
}

async function reload() {
  try { data = await api('/api/gurukul/catalog'); } catch (err) { document.getElementById('gkCatBody').innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; return; }
  render();
}

const header = (title, btn) => `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
  <h2 style="margin:0;">${title}</h2><button type="button" id="gkCatNew">${btn}</button></div>`;

function render() {
  document.querySelectorAll('.tabs-container .tab-btn[data-tab]').forEach(b => { b.style.cssText = tabStyle(b.dataset.tab === tab); });
  const box = document.getElementById('gkCatBody');
  const progName = new Map(data.programs.map(p => [p.id, p.name]));
  let open;

  if (tab === 'programs') {
    open = openProgram;
    box.innerHTML = `<div class="card">${header('Programs', 'New program')}
      <div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Program</th><th>Learning Paths</th><th>Order</th><th>Status</th></tr></thead><tbody>
      ${data.programs.length ? data.programs.map(p => `<tr class="row-clickable" data-id="${p.id}">
          <td data-label="Program"><strong>${esc(p.name)}</strong>${p.description ? '<br><small>' + esc(p.description) + '</small>' : ''}</td>
          <td data-label="Learning Paths">${p.path_ids.length}</td><td data-label="Order">${p.sort_order}</td><td data-label="Status">${activeBadge(p.is_active)}</td></tr>`).join('')
        : '<tr><td colspan="4" class="empty-state">No programs yet.</td></tr>'}
      </tbody></table></div></div>`;
  } else if (tab === 'paths') {
    open = openPath;
    box.innerHTML = `<div class="card">${header('Learning Paths', 'New Learning Path')}
      <div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Code</th><th>Name</th><th>In programs</th><th>Months</th><th>Courses</th><th>Status</th></tr></thead><tbody>
      ${data.paths.length ? data.paths.map(p => `<tr class="row-clickable" data-id="${p.id}">
          <td data-label="Code"><strong>${esc(p.code)}</strong></td><td data-label="Name">${esc(p.name)}</td>
          <td data-label="In programs">${p.program_ids.map(id => esc(progName.get(id) || '?')).join(', ') || '<em>none</em>'}</td>
          <td data-label="Months">${p.months}</td><td data-label="Courses">${p.course_count}</td><td data-label="Status">${activeBadge(p.is_active)}</td></tr>`).join('')
        : '<tr><td colspan="6" class="empty-state">No Learning Paths yet.</td></tr>'}
      </tbody></table></div></div>`;
  } else {
    open = openCertificate;
    box.innerHTML = `<div class="card">${header('Certificates', 'New certificate')}
      <p class="page-intro">Which courses a learner must complete to earn each certificate. Eligibility and printing come later.</p>
      <div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Code</th><th>Name</th><th>Courses</th><th>Status</th></tr></thead><tbody>
      ${data.certificates.length ? data.certificates.map(c => `<tr class="row-clickable" data-id="${c.id}">
          <td data-label="Code"><strong>${esc(c.code)}</strong></td><td data-label="Name">${esc(c.name)}</td>
          <td data-label="Courses">${c.course_count}</td><td data-label="Status">${activeBadge(c.is_active)}</td></tr>`).join('')
        : '<tr><td colspan="4" class="empty-state">No certificates yet.</td></tr>'}
      </tbody></table></div></div>`;
  }
  box.querySelectorAll('tr[data-id]').forEach(tr => tr.addEventListener('click', () => open(tr.dataset.id)));
  document.getElementById('gkCatNew').addEventListener('click', () => open(null));
}

function modal(title, html) {
  document.getElementById('gkCatModalTitle').textContent = title;
  document.getElementById('gkCatModalBody').innerHTML = html;
  document.getElementById('gkCatModal').classList.add('active');
  return document.getElementById('gkCatModalBody');
}

const field = (id, label, value, { max = 160, type = 'text', hint = '' } = {}) => `<div class="form-group"><label for="${id}">${label}</label>
  <input type="${type}" id="${id}" value="${esc(value)}" maxlength="${max}" style="max-width:420px;width:100%;">${hint ? `<small>${hint}</small>` : ''}</div>`;
const area = (id, label, value, max = 1000) => `<div class="form-group"><label for="${id}">${label}</label>
  <textarea id="${id}" maxlength="${max}" rows="2" style="max-width:560px;width:100%;">${esc(value)}</textarea></div>`;
const activeBox = (id, on) => `<div class="form-group"><label style="display:flex;gap:8px;align-items:center;"><input type="checkbox" id="${id}"${on ? ' checked' : ''}> Switched on (offered)</label></div>`;
const val = id => document.getElementById(id).value;
const lineCount = t => t.split(/\r?\n/).filter(l => l.trim()).length;

// ---------- Programs ----------
function openProgram(id) {
  const p = data.programs.find(x => x.id === id) || { name: '', description: '', sort_order: 100, is_active: true, path_ids: [] };
  const body = modal(id ? 'Program — ' + p.name : 'New program', `
    ${field('gkPrName', 'Program name', p.name, { max: 120 })}
    ${area('gkPrDesc', 'Description', p.description)}
    ${field('gkPrSort', 'Order in lists', p.sort_order, { type: 'number' })}
    ${activeBox('gkPrActive', p.is_active)}
    <button type="button" id="gkPrSave">${id ? 'Save changes' : 'Create program'}</button>
    ${id ? `<h3 style="margin-top:20px;">Learning Paths in this program</h3>
      <p class="page-intro">Tick the paths this program offers. A path can be in several programs.</p>
      ${data.paths.length ? `<label style="display:flex;gap:8px;align-items:center;font-weight:bold;"><input type="checkbox" id="gkPrAll"> All</label>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:4px 16px;margin:8px 0 12px;">
        ${data.paths.map(lp => `<label style="display:flex;gap:8px;align-items:center;"><input type="checkbox" class="gkPrPath" value="${lp.id}"${p.path_ids.includes(lp.id) ? ' checked' : ''}>
          ${esc(lp.code)} — ${esc(lp.name)}${lp.is_active ? '' : ' <em>(off)</em>'}</label>`).join('')}
      </div><button type="button" id="gkPrPathsSave">Save Learning Paths</button>` : '<p class="page-intro">Create Learning Paths first (Learning Paths tab).</p>'}` : ''}`);

  document.getElementById('gkPrSave').addEventListener('click', async () => {
    const name = val('gkPrName').trim();
    if (!name) { showToast('Program name is required', 'error'); return; }
    if (!(await showConfirm(`${id ? 'Save changes to' : 'Create'} program <strong>${esc(name)}</strong>?`))) return;
    try {
      const r = await post({ action: 'save_program', id: id || undefined, name, description: val('gkPrDesc'), sort_order: val('gkPrSort'), is_active: document.getElementById('gkPrActive').checked });
      showToast(id ? 'Program saved' : 'Program created — now tick its Learning Paths', 'success');
      await reload(); openProgram(r.id);
    } catch (err) { failed(err); }
  });
  const all = document.getElementById('gkPrAll');
  if (!all) return;
  const boxes = () => [...body.querySelectorAll('.gkPrPath')];
  const sync = () => { all.checked = boxes().every(b => b.checked); };
  sync();
  all.addEventListener('change', () => boxes().forEach(b => { b.checked = all.checked; }));
  boxes().forEach(b => b.addEventListener('change', sync));
  document.getElementById('gkPrPathsSave').addEventListener('click', async () => {
    const ids = boxes().filter(b => b.checked).map(b => b.value);
    if (!(await showConfirm(`Program <strong>${esc(p.name)}</strong> will offer ${ids.length} Learning Path(s). Save?`))) return;
    try { const r = await post({ action: 'set_program_paths', program_id: id, path_ids: ids }); showToast(`Saved: ${r.added} added, ${r.removed} removed`, 'success'); await reload(); openProgram(id); }
    catch (err) { failed(err); }
  });
}

// ---------- Learning Paths ----------
async function openPath(id) {
  const p = data.paths.find(x => x.id === id) || { code: '', name: '', description: '', sort_order: 100, is_active: true, program_ids: [] };
  const shared = p.program_ids.length > 1;
  modal(id ? `Learning Path — ${p.code}` : 'New Learning Path', `
    ${shared ? `<p class="page-intro" style="border-left:4px solid var(--warning, #d98e04);padding-left:10px;"><strong>Used in ${p.program_ids.length} programs.</strong> Changes here apply to all of them.</p>` : ''}
    ${field('gkLpCode', 'Code', p.code, { max: 60, hint: 'Short, no spaces — e.g. SANSKRIT-1' })}
    ${field('gkLpName', 'Name', p.name)}
    ${area('gkLpDesc', 'Description', p.description)}
    ${field('gkLpSort', 'Order in lists', p.sort_order, { type: 'number' })}
    ${activeBox('gkLpActive', p.is_active)}
    <button type="button" id="gkLpSave">${id ? 'Save changes' : 'Create Learning Path'}</button>
    ${id ? `<h3 style="margin-top:20px;">Courses by month</h3><div id="gkLpCourses"><p class="empty-state">Loading…</p></div>
      <h3 style="margin-top:16px;">Add or update courses</h3>
      <p class="page-intro">Paste one course per line: <code>month, course code, course name</code> (the name is optional; a copy from a spreadsheet works too).
        A course already in this path moves to the new month; a new name replaces the old one. If any line is wrong, nothing is saved.</p>
      <textarea id="gkLpPaste" rows="6" style="width:100%;" placeholder="1, NG-BGF-101-A1, Introduction&#10;1, NG-BGF-101-A2, Chanting basics&#10;2, NG-BGF-102-A1, Stories"></textarea>
      <button type="button" id="gkLpImport" style="margin-top:8px;">Add these courses</button>` : ''}`);

  document.getElementById('gkLpSave').addEventListener('click', async () => {
    const code = val('gkLpCode').trim(); const name = val('gkLpName').trim();
    if (!code || !name) { showToast('Code and name are required', 'error'); return; }
    if (!(await showConfirm(`${id ? 'Save changes to' : 'Create'} Learning Path <strong>${esc(code)} — ${esc(name)}</strong>?${shared ? `<br>This changes it in all ${p.program_ids.length} programs.` : ''}`))) return;
    try {
      const r = await post({ action: 'save_path', id: id || undefined, code, name, description: val('gkLpDesc'), sort_order: val('gkLpSort'), is_active: document.getElementById('gkLpActive').checked });
      showToast(id ? 'Learning Path saved' : 'Learning Path created — now add its courses', 'success');
      await reload(); openPath(r.id);
    } catch (err) { failed(err); }
  });
  if (!id) return;

  const loadCourses = async () => {
    const list = document.getElementById('gkLpCourses');
    try {
      const d = await api('/api/gurukul/catalog?path=' + encodeURIComponent(id));
      list.innerHTML = d.courses.length ? `<div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Month</th><th>Course code</th><th>Name</th><th></th></tr></thead><tbody>
        ${d.courses.map(c => `<tr><td data-label="Month">${c.month_no}</td><td data-label="Course code"><code>${esc(c.course_code)}</code></td><td data-label="Name">${esc(c.name) || '<em>no name</em>'}</td>
          <td data-label="" class="action-buttons"><button type="button" class="secondary gkLpRen" data-c="${c.course_id}" data-code="${esc(c.course_code)}" data-name="${esc(c.name)}">Rename</button>
          <button type="button" class="danger gkLpRm" data-c="${c.course_id}" data-code="${esc(c.course_code)}">Remove</button></td></tr>`).join('')}
        </tbody></table></div>` : '<p class="page-intro">No courses yet.</p>';
      list.querySelectorAll('.gkLpRm').forEach(b => b.addEventListener('click', async () => {
        if (!(await showConfirm(`Remove course <strong>${esc(b.dataset.code)}</strong> from ${esc(p.code)}? The course itself is kept; only its place in this path is removed.`))) return;
        try { await post({ action: 'remove_path_course', path_id: id, course_id: b.dataset.c }); showToast('Removed', 'success'); await loadCourses(); reload(); } catch (err) { failed(err); }
      }));
      list.querySelectorAll('.gkLpRen').forEach(b => b.addEventListener('click', async () => {
        const name = await showPrompt(`New name for ${b.dataset.code} — it changes everywhere this course appears.`, { title: 'Rename course', defaultValue: b.dataset.name, okLabel: 'Rename' });
        if (!name) return;
        try { await post({ action: 'rename_course', course_id: b.dataset.c, name }); showToast('Renamed', 'success'); await loadCourses(); } catch (err) { failed(err); }
      }));
    } catch (err) { list.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; }
  };
  await loadCourses();
  document.getElementById('gkLpImport').addEventListener('click', async () => {
    const text = val('gkLpPaste').trim();
    if (!text) { showToast('Paste the course lines first', 'error'); return; }
    if (!(await showConfirm(`Add or update ${lineCount(text)} line(s) in <strong>${esc(p.code)}</strong>?`))) return;
    try {
      const r = await post({ action: 'import_path_courses', path_id: id, text });
      showToast(`Done: ${r.added_to_path} added, ${r.month_changed} moved to another month, ${r.new_courses} new course(s), ${r.renamed} renamed`, 'success');
      document.getElementById('gkLpPaste').value = '';
      await loadCourses(); reload();
    } catch (err) { failed(err); }
  });
}

// ---------- Certificates ----------
async function openCertificate(id) {
  const c = data.certificates.find(x => x.id === id) || { code: '', name: '', description: '', is_active: true };
  modal(id ? `Certificate — ${c.code}` : 'New certificate', `
    ${field('gkCeCode', 'Code', c.code, { max: 60, hint: 'Short, no spaces — e.g. BG-CH5-PART1' })}
    ${field('gkCeName', 'Name (as printed)', c.name, { max: 200 })}
    ${area('gkCeDesc', 'Description', c.description)}
    ${activeBox('gkCeActive', c.is_active)}
    <button type="button" id="gkCeSave">${id ? 'Save changes' : 'Create certificate'}</button>
    ${id ? `<h3 style="margin-top:20px;">Courses required</h3><div id="gkCeCourses"><p class="empty-state">Loading…</p></div>
      <h3 style="margin-top:16px;">Add courses</h3>
      <p class="page-intro">Paste one course code per line. Each code must already be in a Learning Path (this catches typos). Courses may come from different paths.</p>
      <textarea id="gkCePaste" rows="5" style="width:100%;" placeholder="NG-BGF-101-A1&#10;NG-BGF-101-A2"></textarea>
      <button type="button" id="gkCeImport" style="margin-top:8px;">Add these courses</button>` : ''}`);

  document.getElementById('gkCeSave').addEventListener('click', async () => {
    const code = val('gkCeCode').trim(); const name = val('gkCeName').trim();
    if (!code || !name) { showToast('Code and name are required', 'error'); return; }
    if (!(await showConfirm(`${id ? 'Save changes to' : 'Create'} certificate <strong>${esc(code)} — ${esc(name)}</strong>?`))) return;
    try {
      const r = await post({ action: 'save_certificate', id: id || undefined, code, name, description: val('gkCeDesc'), is_active: document.getElementById('gkCeActive').checked });
      showToast(id ? 'Certificate saved' : 'Certificate created — now add its courses', 'success');
      await reload(); openCertificate(r.id);
    } catch (err) { failed(err); }
  });
  if (!id) return;

  const loadCourses = async () => {
    const list = document.getElementById('gkCeCourses');
    try {
      const d = await api('/api/gurukul/catalog?certificate=' + encodeURIComponent(id));
      list.innerHTML = d.courses.length ? `<p class="page-intro">${d.courses.length} course(s).</p><div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Course code</th><th>Name</th><th>In path(s)</th><th></th></tr></thead><tbody>
        ${d.courses.map(x => `<tr><td data-label="Course code"><code>${esc(x.course_code)}</code></td><td data-label="Name">${esc(x.name) || '<em>no name</em>'}</td><td data-label="In path(s)">${esc(x.paths) || '<em>none</em>'}</td>
          <td data-label="" class="action-buttons"><button type="button" class="danger gkCeRm" data-c="${x.course_id}" data-code="${esc(x.course_code)}">Remove</button></td></tr>`).join('')}
        </tbody></table></div>` : '<p class="page-intro">No courses yet.</p>';
      list.querySelectorAll('.gkCeRm').forEach(b => b.addEventListener('click', async () => {
        if (!(await showConfirm(`Remove <strong>${esc(b.dataset.code)}</strong> from certificate ${esc(c.code)}?`))) return;
        try { await post({ action: 'remove_certificate_course', certificate_id: id, course_id: b.dataset.c }); showToast('Removed', 'success'); await loadCourses(); reload(); } catch (err) { failed(err); }
      }));
    } catch (err) { list.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; }
  };
  await loadCourses();
  document.getElementById('gkCeImport').addEventListener('click', async () => {
    const text = val('gkCePaste').trim();
    if (!text) { showToast('Paste the course codes first', 'error'); return; }
    if (!(await showConfirm(`Add ${lineCount(text)} course code(s) to certificate <strong>${esc(c.code)}</strong>?`))) return;
    try {
      const r = await post({ action: 'import_certificate_courses', certificate_id: id, text });
      showToast(`Done: ${r.added} added, ${r.already_there} were already there`, 'success');
      document.getElementById('gkCePaste').value = '';
      await loadCourses(); reload();
    } catch (err) { failed(err); }
  });
}

export async function initGurukulCatalogPage() {
  tab = 'programs';
  document.querySelectorAll('.tabs-container .tab-btn[data-tab]').forEach(b => b.addEventListener('click', () => { tab = b.dataset.tab; render(); }));
  document.getElementById('gkCatClose').addEventListener('click', () => document.getElementById('gkCatModal').classList.remove('active'));
  await reload();
}
